/**
 * Tickets Tab Component
 * Dashboard for event ticket management - QR codes, distribution, campaigns
 */

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../services/supabase";
import { getEventAttendees } from "../../../services/events.service";
import LoadingSpinner from "../../common/LoadingSpinner";
import type { UUID } from "../../../types/common.types";
import {
  TicketIcon,
  QrCodeIcon,
  EnvelopeIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import TicketTiersManager from "../TicketTiersManager";

interface TicketsTabProps {
  eventId: UUID;
  eventTitle?: string;
}

export default function TicketsTab({ eventId }: TicketsTabProps) {
  const navigate = useNavigate();

  // Fetch attendees to get ticket stats
  const { data: attendees = [], isLoading: loadingAttendees } = useQuery({
    queryKey: ["event-attendees", eventId],
    queryFn: () => getEventAttendees(eventId),
  });

  // Fetch campaigns linked to this event
  const { data: campaigns = [], isLoading: loadingCampaigns } = useQuery({
    queryKey: ["event-campaigns", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("*")
        .eq("event_id", eventId)
        .not("is_deleted", "is", true) // soft-deleted campaigns are hidden (migration 203)
        .in("campaign_type", ["event_ticket", "event_reminder"])
        .order("created_at", { ascending: false });
      if (error) {
        throw error;
      }
      return data || [];
    },
  });

  // Calculate ticket statistics
  const registeredCount = attendees.filter(
    (a: any) =>
      a.attendance_status === "registered" ||
      a.attendance_status === "attended",
  ).length;
  const checkedInCount = attendees.filter((a: any) => a.checked_in_at).length;
  const ticketsSentCount = attendees.filter(
    (a: any) => a.ticket_sent_at,
  ).length;
  const pendingTickets = registeredCount - ticketsSentCount;

  const isLoading = loadingAttendees || loadingCampaigns;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Ticket Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <UserGroupIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {registeredCount}
              </div>
              <div className="text-sm text-gray-500">Registered</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <EnvelopeIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {ticketsSentCount}
              </div>
              <div className="text-sm text-gray-500">Tickets Sent</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <ClockIcon className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {pendingTickets}
              </div>
              <div className="text-sm text-gray-500">Pending</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <CheckCircleIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {checkedInCount}
              </div>
              <div className="text-sm text-gray-500">Checked In</div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-medium text-gray-900">Quick Actions</h3>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Create Ticket Campaign */}
            <button
              onClick={() =>
                navigate(
                  `/campaigns/create?type=event_ticket&event_id=${eventId}`,
                )
              }
              className="flex items-center gap-3 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary-400 hover:bg-primary-50 transition-colors text-left"
            >
              <div className="p-2 bg-primary-100 rounded-lg">
                <TicketIcon className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">Send Tickets</div>
                <div className="text-sm text-gray-500">
                  Create ticket campaign
                </div>
              </div>
            </button>

            {/* Create Reminder Campaign */}
            <button
              onClick={() =>
                navigate(
                  `/campaigns/create?type=event_reminder&event_id=${eventId}`,
                )
              }
              className="flex items-center gap-3 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary-400 hover:bg-primary-50 transition-colors text-left"
            >
              <div className="p-2 bg-yellow-100 rounded-lg">
                <ClockIcon className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">Send Reminder</div>
                <div className="text-sm text-gray-500">
                  Create reminder campaign
                </div>
              </div>
            </button>

            {/* Open Check-in Scanner */}
            <a
              href={`/event-checkin?event_id=${eventId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-green-400 hover:bg-green-50 transition-colors text-left"
            >
              <div className="p-2 bg-green-100 rounded-lg">
                <QrCodeIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="font-medium text-gray-900">QR Scanner</div>
                <div className="text-sm text-gray-500">Open check-in page</div>
              </div>
              <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400 ml-auto" />
            </a>
          </div>
        </div>
      </div>

      {/* Ticket Campaigns */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-medium text-gray-900">Ticket Campaigns</h3>
          <button
            onClick={() =>
              navigate(
                `/campaigns/create?type=event_ticket&event_id=${eventId}`,
              )
            }
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            <PlusIcon className="h-4 w-4" />
            New Campaign
          </button>
        </div>

        {campaigns.length === 0 ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <TicketIcon className="h-6 w-6 text-gray-400" />
            </div>
            <h4 className="text-gray-900 font-medium mb-1">
              No ticket campaigns yet
            </h4>
            <p className="text-sm text-gray-500 mb-4">
              Create a campaign to send tickets to registered attendees
            </p>
            <button
              onClick={() =>
                navigate(
                  `/campaigns/create?type=event_ticket&event_id=${eventId}`,
                )
              }
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Create Ticket Campaign
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {campaigns.map((campaign: any) => (
              <div
                key={campaign.id}
                className="p-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/campaigns/${campaign.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        campaign.campaign_type === "event_ticket"
                          ? "bg-purple-100"
                          : "bg-yellow-100"
                      }`}
                    >
                      {campaign.campaign_type === "event_ticket" ? (
                        <TicketIcon
                          className={`h-5 w-5 ${
                            campaign.campaign_type === "event_ticket"
                              ? "text-purple-600"
                              : "text-yellow-600"
                          }`}
                        />
                      ) : (
                        <ClockIcon className="h-5 w-5 text-yellow-600" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {campaign.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {campaign.campaign_type === "event_ticket"
                          ? "Ticket Distribution"
                          : "Event Reminder"}
                        {" · "}
                        {new Date(campaign.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {campaign.total_vouchers_generated || 0} sent
                      </div>
                      <div className="text-xs text-gray-500">
                        {campaign.total_vouchers_redeemed || 0} opened
                      </div>
                    </div>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        campaign.status === "active"
                          ? "bg-green-100 text-green-800"
                          : campaign.status === "completed"
                            ? "bg-blue-100 text-blue-800"
                            : campaign.status === "draft"
                              ? "bg-gray-100 text-gray-800"
                              : "bg-red-100 text-red-800"
                      }`}
                    >
                      {campaign.status}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Distribution Progress */}
      {registeredCount > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Distribution Progress</h3>
            <span className="text-sm text-gray-500">
              {Math.round((ticketsSentCount / registeredCount) * 100)}% complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div
              className="bg-primary-600 h-3 rounded-full transition-all duration-300"
              style={{
                width: `${(ticketsSentCount / registeredCount) * 100}%`,
              }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>{ticketsSentCount} tickets sent</span>
            <span>{pendingTickets} pending</span>
          </div>
        </div>
      )}

      {/* Check-in Progress */}
      {ticketsSentCount > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900">Check-in Progress</h3>
            <span className="text-sm text-gray-500">
              {Math.round((checkedInCount / registeredCount) * 100)}% checked in
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
            <div
              className="bg-green-600 h-3 rounded-full transition-all duration-300"
              style={{
                width: `${(checkedInCount / registeredCount) * 100}%`,
              }}
            ></div>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>{checkedInCount} checked in</span>
            <span>{registeredCount - checkedInCount} remaining</span>
          </div>
        </div>
      )}

      {/* Ticket Tiers Management */}
      <div className="pt-6 border-t border-gray-200">
        <TicketTiersManager eventId={eventId} />
      </div>
    </div>
  );
}
